import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "@phosphor-icons/react";
import api from "@/lib/api";
import CreatorCard from "@/components/CreatorCard";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1500305614571-ae5b6592e65d?crop=entropy&cs=srgb&fm=jpg&q=80&w=1800";
const AUDIO_IMG = "https://images.pexels.com/photos/31050644/pexels-photo-31050644.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";
const EASE = [0.16, 1, 0.3, 1];

const CHAPTERS = [
  { n: "01", t: "For the connoisseurs", d: "Feet, lingerie, latex and everything whispered after dark. Curated by creators who own their craft." },
  { n: "02", t: "Fantasy you can hear", d: "Original spoken-word stories and audiobooks, voiced by the creators themselves. Press play, close your eyes." },
  { n: "03", t: "Direct & intimate", d: "Message your favorites, unlock private drops, and support the artists behind the fantasy — directly." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [creators, setCreators] = useState([]);
  const [categories, setCategories] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 240]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    api.get("/creators").then(({ data }) => setCreators(data.slice(0, 6))).catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] flex items-end overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-background/70" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/80 to-transparent" />
        </motion.div>

        <motion.div style={{ opacity }} className="relative z-10 w-full max-w-7xl mx-auto px-5 pb-16 md:pb-24">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 0.8 }}
            className="font-accent text-xs uppercase tracking-[0.5em] text-primary mb-6"
          >
            ISeeFantasy.Com · See it your way
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 40, clipPath: "inset(0 100% 0 0)" }}
            animate={{ opacity: 1, y: 0, clipPath: "inset(0 0% 0 0)" }}
            transition={{ duration: 1.1, delay: 0.3, ease: EASE }}
            data-testid="hero-logo"
            className="flex items-center gap-3 md:gap-5"
          >
            <img
              src="/logo-mark-transparent.png"
              alt=""
              className="h-16 md:h-24 lg:h-28 w-auto select-none pointer-events-none drop-shadow-[0_0_40px_rgba(255,0,85,0.35)]"
            />
            <span className="font-display text-6xl md:text-8xl lg:text-9xl leading-none tracking-tight">
              ISEE<span className="italic text-primary">FANTASY</span>
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.8, ease: EASE }}
            className="font-display text-3xl md:text-5xl lg:text-6xl leading-[0.9] tracking-tight mt-4"
          >
            Desire, <span className="italic text-primary">unfiltered</span>.
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 flex flex-col sm:flex-row gap-4 sm:items-center"
          >
            <button
              data-testid="hero-cta"
              onClick={() => navigate(user ? "/explore" : "/register")}
              className="group inline-flex items-center gap-3 px-8 py-4 bg-primary text-primary-foreground font-accent font-bold uppercase tracking-widest text-sm w-fit transition-transform active:scale-95 hover:bg-[#e6004c]"
            >
              {user ? "Explore creators" : "Start exploring"}
              <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </button>
            <p className="text-sm text-muted-foreground max-w-xs">
              Thousands of creators. Feet, lingerie, fantasy audio & more — all in one place.
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3 }}
          className="absolute bottom-6 right-6 z-10 hidden md:flex items-center gap-2 text-muted-foreground"
        >
          <span className="font-accent text-xs uppercase tracking-widest">Scroll</span>
          <ArrowDown size={16} className="animate-bounce" />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="py-6 border-y border-border bg-card overflow-hidden">
        <div className="animate-marquee flex gap-8 will-change-transform">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex gap-8 items-center shrink-0">
              {["Feet", "Lingerie", "Fantasy Audio", "Cosplay", "Leather & Latex", "Voyeur", "Whispered Stories"].map((w) => (
                <span key={w} className="font-display italic text-3xl md:text-5xl text-foreground/80 flex items-center gap-8">
                  {w} <span className="text-primary not-italic">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 py-20 md:py-28">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-display text-4xl md:text-6xl leading-none">Pick your <span className="italic text-primary">pleasure</span></h2>
          <Link to="/explore" data-testid="cats-viewall" className="font-accent text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">All →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {categories.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
            >
              <Link
                to={`/explore?category=${c.slug}`}
                data-testid={`cat-${c.slug}`}
                className="group relative flex items-end h-36 md:h-44 p-4 bg-card border border-border hover:border-primary/50 transition-colors overflow-hidden"
              >
                <span className="absolute top-3 right-3 font-accent text-xs text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <p className="font-display text-2xl md:text-3xl group-hover:text-primary transition-colors">{c.label}</p>
                  <p className="text-xs text-muted-foreground">{c.creators} creators</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="border-y border-border bg-card">
        <div className="max-w-7xl mx-auto px-5 py-20 md:py-28 grid md:grid-cols-3 gap-px">
          {CHAPTERS.map((ch, i) => (
            <motion.div
              key={ch.n}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: EASE }}
              className="px-2 md:px-6"
            >
              <p className="font-accent text-primary text-sm tracking-widest mb-6">{ch.n}</p>
              <h3 className="font-display text-3xl md:text-4xl mb-4 leading-tight">{ch.t}</h3>
              <p className="text-muted-foreground leading-relaxed">{ch.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* AUDIO FEATURE */}
      <section className="max-w-7xl mx-auto px-5 py-20 md:py-28 grid md:grid-cols-2 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE }}
        >
          <p className="font-accent text-xs uppercase tracking-[0.4em] text-primary mb-5">Fantasy Audio</p>
          <h2 className="font-display text-5xl md:text-6xl leading-none mb-6">
            Stories, <span className="italic">whispered</span> just for you.
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed mb-8 max-w-md">
            Original erotic audiobooks and spoken-word fantasies, narrated by the creators themselves.
            Slip on your headphones and let their voice take over.
          </p>
          <button
            data-testid="audio-cta"
            onClick={() => navigate("/explore?post_type=audio")}
            className="inline-flex items-center gap-3 px-8 py-4 border border-foreground text-foreground font-accent font-bold uppercase tracking-widest text-sm transition-colors hover:bg-foreground hover:text-background"
          >
            Listen now <ArrowRight size={18} weight="bold" />
          </button>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: EASE }}
          className="relative aspect-[4/5] overflow-hidden border border-border"
        >
          <img src={AUDIO_IMG} alt="Studio microphone" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        </motion.div>
      </section>

      {/* FEATURED CREATORS */}
      {creators.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 pb-24">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-display text-4xl md:text-6xl leading-none">Featured <span className="italic text-primary">talent</span></h2>
            <Link to="/explore" data-testid="creators-viewall" className="font-accent text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">See all →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {creators.map((c, i) => <CreatorCard key={c.id} creator={c} index={i} />)}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-t border-border">
        <div className="max-w-7xl mx-auto px-5 py-24 md:py-36 text-center relative overflow-hidden">
          <motion.h2
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: EASE }}
            className="font-display text-6xl md:text-8xl lg:text-[9vw] leading-[0.85] tracking-tighter mb-8"
          >
            Turn desire<br /><span className="italic text-primary">into income.</span>
          </motion.h2>
          <p className="text-muted-foreground text-lg max-w-lg mx-auto mb-10">
            Creators keep the spotlight — set your price, drop exclusive content, and get paid directly.
          </p>
          <button
            data-testid="cta-become-creator"
            onClick={() => navigate("/register")}
            className="inline-flex items-center gap-3 px-10 py-5 bg-primary text-primary-foreground font-accent font-bold uppercase tracking-widest text-sm transition-transform active:scale-95 hover:bg-[#e6004c]"
          >
            Become a creator <ArrowRight size={18} weight="bold" />
          </button>
        </div>
      </section>

      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-display text-xl">ISEE<span className="italic text-primary">FANTASY</span></p>
          <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} ISEEFANTASY · Adults only (21+). All creators are verified.</p>
        </div>
      </footer>
    </div>
  );
}
